import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Columns3,
  Database,
  Download,
  Eye,
  FileArchive,
  FileJson,
  FileUp,
  Filter,
  FolderOpen,
  Gauge,
  GitMerge,
  GitCompareArrows,
  Grid3X3,
  HardDrive,
  Info,
  Keyboard,
  Layers3,
  Loader2,
  Menu,
  Moon,
  PanelRight,
  Play,
  Plus,
  Radar,
  RefreshCw,
  Save,
  Search,
  Scissors,
  Send,
  Settings,
  ShieldCheck,
  SkipBack,
  SkipForward,
  Sparkles,
  Square,
  Tags,
  UploadCloud,
  Undo2,
  Wand2,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AuraTelemetryEventLog, ProoflineStatus } from "@auraone/aura-ide-kit";
import { commands, datasets, probeTrials } from "./data";
import {
  buildVirtualWindow,
  clampTime,
  defaultFilters,
  defaultSort,
  exportManifest,
  formatTime,
  nextSuccessState,
  readinessTone,
  summarizeDataset,
  visibleEpisodes,
} from "./core";
import {
  artifactPlan,
  buildDeterministicClusters,
  buildLocalEvidenceArchive,
  buildProbeReport,
  buildSensorQaReport,
  buildSensorQaMarkdown,
  createDatasetFromLocalFiles,
  downloadBinaryArtifact,
  downloadTextArtifact,
  mergeClusterRows,
  recomputeDeterministicClusters,
  refreshClusterRows,
  splitClusterRows,
  summarizeClusterRows,
  type ExportScope,
  type LocalFileEntry,
} from "./localOperations";
import {
  LocalDiagnosticEventBuffer,
  ROBOTICS_PLATFORM_HOOKS,
  createRoboticsDiagnosticEvent,
  ensureRoboticsIntakeInstallSigningKeypair,
  roboticsIntakeInstallSigningKeypairKey,
  toAuraTelemetryEvents,
  type LocalDiagnosticEntry,
} from "./platformContracts";
import type {
  Cluster,
  CommandId,
  DatasetTab,
  Density,
  Episode,
  ExportTarget,
  FilterState,
  Intervention,
  SensorStream,
  SortField,
  SortState,
  ViewMode,
} from "./types";

import {
  Chip,
  LocalDiagnosticEventLog,
  ManifestMetadataScene,
  PanelTitle,
  StateLine,
  SyntheticSensorScene,
  UpdateCenter,
  appVersion,
} from "./components";
import type { ViewBody } from "./views-release";
export function SettingsView({
  diagnosticBufferEnabled,
  setDiagnosticBufferEnabled,
  diagnosticEntries,
  mediaDark,
  setMediaDark,
  networkOnline,
  updateState,
  setUpdateState,
}: Pick<
  Parameters<typeof ViewBody>[0],
  | "diagnosticBufferEnabled"
  | "setDiagnosticBufferEnabled"
  | "diagnosticEntries"
  | "mediaDark"
  | "setMediaDark"
  | "networkOnline"
  | "updateState"
  | "setUpdateState"
>) {
  const [intakeIdentityStatus, setIntakeIdentityStatus] = useState(
    "Checking desktop keychain",
  );

  useEffect(() => {
    let cancelled = false;
    void ensureRoboticsIntakeInstallSigningKeypair()
      .then((keypair) => {
        if (!cancelled) {
          setIntakeIdentityStatus(
            `Ed25519 identity stored in the OS keychain · created ${new Date(
              keypair.created_at,
            ).toLocaleDateString()}`,
          );
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setIntakeIdentityStatus(
            error instanceof Error
              ? error.message
              : "Intake identity is unavailable.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="surface settings-surface"
      aria-label="Settings"
      tabIndex={0}
    >
      <div className="section-head">
        <div>
          <h1>Settings</h1>
          <p>
            Inspect local diagnostic buffering, source-build boundaries, and
            shared platform declarations.
          </p>
        </div>
      </div>
      <div className="qa-grid">
        <StateLine
          label="Local diagnostic buffer"
          status={diagnosticBufferEnabled ? "pass" : "warn"}
          detail={
            diagnosticBufferEnabled
              ? "In-memory preview enabled; delivery is not configured or attempted"
              : "Disabled; no diagnostic events are retained"
          }
        />
        <StateLine
          label="Intake install identity"
          status={intakeIdentityStatus.startsWith("Ed25519") ? "pass" : "warn"}
          detail={`${intakeIdentityStatus} · scope ${roboticsIntakeInstallSigningKeypairKey.scope}`}
        />
        <StateLine
          label="Archive signing"
          status="warn"
          detail="Unavailable in this source build; no key is generated or stored"
        />
        <StateLine
          label="Release channel"
          status="warn"
          detail="Source build / unpublished"
        />
        <StateLine label="License" status="pass" detail="MIT" />
      </div>
      <div className="settings-grid">
        <section aria-labelledby="appearance-settings">
          <h2 id="appearance-settings">Media inspection</h2>
          <p>
            The application shell remains light. Dark contrast is bounded to
            camera, depth, and synchronized media surfaces.
          </p>
          <label className="setting-line">
            <span>Dark media surface</span>
            <input
              type="checkbox"
              checked={mediaDark}
              onChange={(event) => setMediaDark(event.target.checked)}
            />
          </label>
        </section>
        <section aria-labelledby="data-boundary-settings">
          <h2 id="data-boundary-settings">Data and network</h2>
          <ProoflineStatus tone="info" label="Dataset processing is local" />
          <ProoflineStatus
            tone={networkOnline ? "info" : "blocked"}
            label={
              networkOnline
                ? "Browser online; export destinations remain unverified"
                : "Browser offline; local workflows remain available"
            }
          />
        </section>
      </div>
      <label className="setting-line">
        <span>Retain local diagnostic previews in memory</span>
        <input
          type="checkbox"
          checked={diagnosticBufferEnabled}
          onChange={(event) => setDiagnosticBufferEnabled(event.target.checked)}
        />
      </label>
      <UpdateCenter state={updateState} setState={setUpdateState} />
      <LocalDiagnosticEventLog entries={diagnosticEntries} />
      <article className="manifest">
        <h2>Shared platform declarations</h2>
        <p>
          These identifiers are available to the source UI. No native robotics
          engine is connected.
        </p>
        <pre
          role="region"
          tabIndex={0}
          aria-label="Shared platform declarations JSON"
        >
          {JSON.stringify(ROBOTICS_PLATFORM_HOOKS, null, 2)}
        </pre>
      </article>
    </section>
  );
}


export function BrowseView({
  density,
  setDensity,
  visibleGrid,
  filteredEpisodes,
  gridOffset,
  setGridOffset,
  chooseEpisode,
}: Pick<
  Parameters<typeof ViewBody>[0],
  | "density"
  | "setDensity"
  | "visibleGrid"
  | "filteredEpisodes"
  | "gridOffset"
  | "setGridOffset"
  | "chooseEpisode"
>) {
  const failures = filteredEpisodes.filter(
    (episode) => episode.success === "failure",
  ).length;
  const needsReview = filteredEpisodes.filter(
    (episode) => episode.reviewed !== "reviewed",
  ).length;
  const qaWarnings = filteredEpisodes.filter(
    (episode) =>
      episode.sensorQaStatus === "warn" || episode.sensorQaStatus === "fail",
  ).length;
  const qaUnknown = filteredEpisodes.filter(
    (episode) => episode.sensorQaStatus === "unknown",
  ).length;
  const knownReadiness = filteredEpisodes.flatMap((episode) =>
    episode.readiness === null ? [] : [episode.readiness],
  );
  const avgReadiness = knownReadiness.length
    ? Math.round(
        knownReadiness.reduce((total, value) => total + value, 0) /
          knownReadiness.length,
      )
    : null;

  return (
    <section className="surface" aria-label="Episode browser" tabIndex={0}>
      <div className="section-head">
        <div>
          <h1>Dataset browser</h1>
          <p>
            Prioritize the episodes that need a human decision before export.
          </p>
        </div>
        <div className="segmented" role="group" aria-label="Grid density">
          {(["comfortable", "compact", "ultra"] as Density[]).map((option) => (
            <button
              key={option}
              className={density === option ? "active" : ""}
              onClick={() => setDensity(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div
        className="review-summary"
        role="region"
        aria-label="Filtered review summary"
      >
        <article className="lead">
          <span>Visible episodes</span>
          <strong>{filteredEpisodes.length.toLocaleString()}</strong>
          <em>of {filteredEpisodes.length.toLocaleString()} filtered</em>
        </article>
        <article>
          <span>Needs review</span>
          <strong>{needsReview.toLocaleString()}</strong>
        </article>
        <article>
          <span>Failures</span>
          <strong className={failures ? "tone-fail" : ""}>
            {failures.toLocaleString()}
          </strong>
        </article>
        <article>
          <span>Avg readiness</span>
          <strong>{avgReadiness ?? "Unknown"}</strong>
        </article>
        <article>
          <span>Sensor QA</span>
          <strong className={qaWarnings ? "tone-warn" : ""}>
            {qaUnknown
              ? `${qaWarnings} + ${qaUnknown} unknown`
              : qaWarnings.toLocaleString()}
          </strong>
        </article>
      </div>
      <div className={`episode-grid ${density}`}>
        {visibleGrid.map((episode) => (
          <EpisodeCard
            key={episode.id}
            episode={episode}
            onSelect={chooseEpisode}
          />
        ))}
      </div>
      <div className="pager">
        <button
          onClick={() =>
            setGridOffset(Math.max(0, gridOffset - visibleGrid.length))
          }
          disabled={gridOffset === 0}
          title={
            gridOffset === 0
              ? "The first episode window is already visible."
              : undefined
          }
        >
          <SkipBack size={15} /> Previous window
        </button>
        <span>
          Rows {gridOffset + 1}-
          {Math.min(gridOffset + visibleGrid.length, filteredEpisodes.length)}{" "}
          of {filteredEpisodes.length}
        </span>
        <button
          onClick={() =>
            setGridOffset(
              Math.min(
                Math.max(0, filteredEpisodes.length - visibleGrid.length),
                gridOffset + visibleGrid.length,
              ),
            )
          }
          disabled={gridOffset + visibleGrid.length >= filteredEpisodes.length}
          title={
            gridOffset + visibleGrid.length >= filteredEpisodes.length
              ? "The final episode window is already visible."
              : undefined
          }
        >
          Next window <SkipForward size={15} />
        </button>
      </div>
    </section>
  );
}


export function SensorPreview({
  size = "card",
  embodiment,
  badge,
  foot,
  synthetic = true,
}: {
  size?: "card" | "cluster" | "compare" | "plane" | "banner";
  embodiment?: string | null;
  badge?: { tone: "pass" | "warn" | "fail" | "neutral"; label: string };
  foot?: { label: string; meta?: string };
  synthetic?: boolean;
}) {
  return (
    <div className={`sensor-preview ${size}`}>
      {synthetic ? (
        <SyntheticSensorScene sensorId="cam-front" compact />
      ) : (
        <ManifestMetadataScene compact />
      )}
      {badge || embodiment ? (
        <div className="sensor-preview-overlay">
          {badge ? (
            <span className={`episode-state ${badge.tone}`}>
              <i />
              {badge.label}
            </span>
          ) : (
            <span />
          )}
          {embodiment ? (
            <span className="episode-embodiment">{embodiment}</span>
          ) : null}
        </div>
      ) : null}
      {foot ? (
        <div className="sensor-preview-foot">
          <span className="episode-task">{foot.label}</span>
          {foot.meta ? (
            <span className="episode-duration">{foot.meta}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


export function ViewContext({
  dataset,
  summary,
  label,
}: {
  dataset: DatasetTab;
  summary: ReturnType<typeof summarizeDataset>;
  label: string;
}) {
  return (
    <div className="view-context" role="group" aria-label="Dataset context">
      <SensorPreview
        size="banner"
        embodiment={dataset.format}
        synthetic={dataset.provenance.kind === "repository-synthetic-fixture"}
      />
      <div className="view-context-meta">
        <span className="view-context-eyebrow">{label}</span>
        <strong className="view-context-name">{dataset.name}</strong>
        <span className="view-context-root">{dataset.root}</span>
      </div>
      <dl className="view-context-stats">
        <div>
          <dt>Episodes</dt>
          <dd>
            {dataset.episodes.length}
            <small>/{dataset.indexed.toLocaleString()}</small>
          </dd>
        </div>
        <div>
          <dt>Avg readiness</dt>
          <dd className={`tone-${readinessTone(summary.avgReadiness)}`}>
            {summary.avgReadiness ?? "Unknown"}
          </dd>
        </div>
        <div>
          <dt>Failures</dt>
          <dd className={summary.failures ? "tone-fail" : ""}>
            {summary.failures}
          </dd>
        </div>
        <div>
          <dt>QA flags</dt>
          <dd className={summary.qaFailures ? "tone-warn" : ""}>
            {summary.qaUnknown
              ? `${summary.qaFailures} + ${summary.qaUnknown} unknown`
              : summary.qaFailures}
          </dd>
        </div>
      </dl>
    </div>
  );
}


export function interventionTone(why: Intervention["why"]): "fail" | "warn" | "info" {
  if (why === "safety stop") return "fail";
  if (why === "detected anomaly" || why === "manual override") return "warn";
  return "info";
}


export function InterventionStrip({ episode }: { episode: Episode }) {
  const duration = episode.duration;
  if (episode.interventionCount === null || duration === null) {
    return (
      <span className="intervention-empty" aria-hidden="true">
        unknown
      </span>
    );
  }
  if (episode.interventionCount === 0) {
    return (
      <span className="intervention-empty" aria-hidden="true">
        no interventions
      </span>
    );
  }
  return (
    <span className="intervention-strip" aria-hidden="true">
      {episode.interventions.map((intervention) =>
        intervention.start === null || intervention.end === null ? null : (
          <i
            key={intervention.id}
            className={`tone-${interventionTone(intervention.why)}`}
            style={{
              left: `${(intervention.start / duration) * 100}%`,
              width: `${Math.max(4, ((intervention.end - intervention.start) / duration) * 100)}%`,
            }}
          />
        ),
      )}
    </span>
  );
}


export function EpisodeCard({
  episode,
  onSelect,
}: {
  episode: Episode;
  onSelect: (episode: Episode) => void;
}) {
  const successTone =
    episode.success === "success"
      ? "pass"
      : episode.success === "failure"
        ? "fail"
        : "warn";
  const qaTone =
    episode.sensorQaStatus === "pass"
      ? "pass"
      : episode.sensorQaStatus === "fail"
        ? "fail"
        : episode.sensorQaStatus === "warn"
          ? "warn"
          : "neutral";
  const sensorBadges = episode.sensors.slice(0, 3);
  const readinessClass = readinessTone(episode.readiness);
  const taskLabel = episode.task
    ? episode.task.replaceAll("_", " ")
    : "Task unknown";
  const readinessLabel =
    episode.readiness === null ? "Unknown" : `${episode.readiness}`;
  return (
    <button
      className="episode-card"
      onClick={() => onSelect(episode)}
      aria-label={`Open ${episode.id}`}
    >
      <SensorPreview
        size="card"
        embodiment={episode.embodiment}
        badge={{ tone: successTone, label: episode.success }}
        foot={{ label: taskLabel, meta: formatTime(episode.duration) }}
        synthetic={episode.provenance === "repository-synthetic-fixture"}
      />
      <div className="episode-card-body">
        <div className="episode-card-topline">
          <strong>{episode.id}</strong>
          <span className={`episode-readiness-pill ${readinessClass}`}>
            {readinessLabel}
          </span>
        </div>
        <div className="readiness-track" aria-hidden="true">
          <i style={{ width: `${episode.readiness ?? 0}%` }} />
        </div>
        <div className="episode-meta">
          <span className="meta-cell meta-interventions">
            <CircleDot size={11} />
            <span className="meta-label">
              {episode.interventionCount === null
                ? "intv unknown"
                : `${episode.interventionCount} intv`}
            </span>
            <InterventionStrip episode={episode} />
          </span>
          <span className="meta-cell">
            <ShieldCheck size={11} /> QA {episode.sensorQaStatus}
          </span>
        </div>
        <div className="sensor-dots" aria-hidden="true">
          {sensorBadges.map((sensor) => (
            <span
              key={sensor.id}
              className={`sensor-dot ${sensor.kind}`}
              title={sensor.label}
            />
          ))}
          {episode.sensors.length > sensorBadges.length ? (
            <span className="sensor-dot-more">
              +{episode.sensors.length - sensorBadges.length}
            </span>
          ) : null}
          <span className={`chip-mini ${qaTone}`}>
            {episode.reviewed === "reviewed"
              ? "reviewed"
              : episode.reviewed === "unreviewed"
                ? "needs review"
                : "review unknown"}
          </span>
        </div>
      </div>
    </button>
  );
}


export function FilterControls({
  filters,
  sort,
  setFilters,
  setSort,
}: {
  filters: FilterState;
  sort: SortState;
  setFilters: (partial: Partial<FilterState>) => void;
  setSort: (sort: SortState) => void;
}) {
  return (
    <div className="filter-stack">
      <label>
        <span>Search</span>
        <input
          value={filters.query}
          onChange={(event) => setFilters({ query: event.target.value })}
          placeholder="episode, task, tag"
        />
      </label>
      <label>
        <span>Success</span>
        <select
          value={filters.success}
          onChange={(event) =>
            setFilters({
              success: event.target.value as FilterState["success"],
            })
          }
        >
          <option>all</option>
          <option>success</option>
          <option>failure</option>
          <option>unknown</option>
        </select>
      </label>
      <label>
        <span>Reviewed</span>
        <select
          value={filters.reviewed}
          onChange={(event) =>
            setFilters({
              reviewed: event.target.value as FilterState["reviewed"],
            })
          }
        >
          <option>all</option>
          <option>reviewed</option>
          <option>unreviewed</option>
          <option>unknown</option>
        </select>
      </label>
      <label>
        <span>Sensor QA</span>
        <select
          value={filters.qa}
          onChange={(event) =>
            setFilters({ qa: event.target.value as FilterState["qa"] })
          }
        >
          <option>all</option>
          <option>pass</option>
          <option>warn</option>
          <option>fail</option>
        </select>
      </label>
      <label>
        <span>Min interventions</span>
        <input
          type="number"
          min={0}
          value={filters.minInterventions}
          onChange={(event) =>
            setFilters({ minInterventions: Number(event.target.value) })
          }
        />
      </label>
      <label>
        <span>Max seconds</span>
        <input
          type="number"
          min={1}
          value={filters.maxLength}
          onChange={(event) =>
            setFilters({ maxLength: Number(event.target.value) })
          }
        />
      </label>
      <label>
        <span>Sort</span>
        <select
          value={sort.field}
          onChange={(event) =>
            setSort({ ...sort, field: event.target.value as SortField })
          }
        >
          <option>readiness</option>
          <option>duration</option>
          <option>interventionCount</option>
          <option>sensorQaStatus</option>
          <option>date</option>
          <option>id</option>
        </select>
      </label>
      <button
        onClick={() =>
          setSort({
            ...sort,
            direction: sort.direction === "asc" ? "desc" : "asc",
          })
        }
      >
        Direction: {sort.direction} <ChevronDown size={14} />
      </button>
    </div>
  );
}


export function SensorToggles({
  episode,
  updateEpisode,
}: {
  episode: Episode;
  updateEpisode: (episodeId: string, partial: Partial<Episode>) => void;
}) {
  return (
    <div className="sensor-list">
      {episode.sensors.map((sensor, index) => (
        <button
          key={sensor.id}
          className={sensor.visible ? "active" : ""}
          onClick={() =>
            updateEpisode(episode.id, {
              sensors: episode.sensors.map((entry) =>
                entry.id === sensor.id
                  ? { ...entry, visible: !entry.visible }
                  : entry,
              ),
            })
          }
        >
          <span>
            {index + 1}. {sensor.label}
          </span>
          <Chip
            label={sensor.status}
            tone={
              sensor.status === "pass"
                ? "pass"
                : sensor.status === "fail"
                  ? "fail"
                  : "warn"
            }
          />
        </button>
      ))}
    </div>
  );
}
